import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Navigation, Phone, Copy, Check, Clock, ShieldCheck, RotateCcw, ZoomIn, ZoomOut, Compass, Search, RefreshCw } from 'lucide-react';

interface MapRegisterProps {
  setMap: (map: L.Map) => void;
}

const MapRegister: React.FC<MapRegisterProps> = ({ setMap }) => {
  const map = useMap();
  useEffect(() => {
    if (map) {
      setMap(map);
    }
  }, [map, setMap]);
  return null;
};

interface MapControllerProps {
  center: { lat: number; lng: number };
}

const MapController: React.FC<MapControllerProps> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (map) {
      map.setView([center.lat, center.lng], 17);
    }
  }, [center, map]);
  return null;
};

export const OfficeLocationMap: React.FC = () => {
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTileStyle, setActiveTileStyle] = useState<'dark' | 'streets' | 'satellite'>('dark');
  const [isGeocoding, setIsGeocoding] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Exact location coordinates for Access Tech Headquarters
  const [coords, setCoords] = useState<{ lat: number; lng: number }>({
    lat: 5.5320,
    lng: 7.4860
  });

  const [addressQuery, setAddressQuery] = useState("2 Abiriba St, Uzuokoli Road, Umuahia, Abia State, Nigeria");
  const [resolvedLocationName, setResolvedLocationName] = useState("2 Abiriba St, Uzuokoli Rd, Umuahia");

  const fullAddress = "2 Abiriba St, Uzuokoli Road, Umuahia, 440233, Abia State, Nigeria";
  const displayAddress = "2 Abiriba St, Uzuokoli Road, Umuahia, 440233";
  const phoneNumber = "0806 475 2074";

  // Google Maps directions
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent("2 Abiriba St, Uzuokoli Road, Umuahia, Abia State, Nigeria")}`;

  const tileUrls = {
    dark: {
      url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      maxZoom: 19
    },
    streets: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19
    },
    satellite: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attribution: 'Esri World Imagery',
      maxZoom: 18
    }
  };

  // Custom Leaflet DivIcon with beautiful green color and ping animations
  const customIcon = React.useMemo(() => {
    if (typeof window === 'undefined' || !L || !L.divIcon) return undefined as any;
    return L.divIcon({
      className: 'custom-leaflet-marker',
      html: `
        <div style="position: relative; display: flex; align-items: center; justify-content: center; width: 44px; height: 44px;">
          <div style="position: absolute; width: 44px; height: 44px; border-radius: 50%; background: rgba(16, 185, 129, 0.4); animation: ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite;"></div>
          <div style="position: relative; width: 32px; height: 32px; border-radius: 50%; background: #059669; border: 3px solid #ffffff; box-shadow: 0 0 15px rgba(16, 185, 129, 0.8); display: flex; align-items: center; justify-content: center; color: #ffffff;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
        </div>
      `,
      iconSize: [44, 44],
      iconAnchor: [22, 22],
      popupAnchor: [0, -22]
    });
  }, []);

  // Perform geocoding against Nominatim API to get the exact real map location
  const geocodeAddress = async (searchStr: string) => {
    setIsGeocoding(true);
    try {
      // Step 1: Try full specific query
      let res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchStr)}`);
      let data = await res.json();

      // Step 2: Fallback query if specific building query returns 0 items
      if ((!data || data.length === 0) && searchStr.includes("Abiriba")) {
        res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent("Uzuakoli Road, Umuahia, Abia State, Nigeria")}`);
        data = await res.json();
      }

      if ((!data || data.length === 0)) {
        res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent("Umuahia, Abia State, Nigeria")}`);
        data = await res.json();
      }

      if (data && data.length > 0) {
        const topResult = data[0];
        const newLat = parseFloat(topResult.lat);
        const newLng = parseFloat(topResult.lon);

        setCoords({ lat: newLat, lng: newLng });
        if (topResult.display_name) {
          setResolvedLocationName(topResult.display_name.split(',').slice(0, 3).join(','));
        }
      }
    } catch (err) {
      console.warn("Geocoding fetch notice, maintaining exact coordinates:", err);
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleZoomIn = () => {
    if (mapInstance) mapInstance.zoomIn();
  };

  const handleZoomOut = () => {
    if (mapInstance) mapInstance.zoomOut();
  };

  const handleRecenter = () => {
    if (mapInstance) {
      mapInstance.setView([coords.lat, coords.lng], 17);
    }
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(fullAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (addressQuery.trim()) {
      geocodeAddress(addressQuery);
    }
  };

  return (
    <div id="office-location-map" className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl text-white space-y-6 my-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2">
            <Compass className="w-3.5 h-3.5 text-emerald-400" /> Interactive Head Office Map
          </div>
          <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-2">
            Access Technologies and Renewable Energy Headquarters
          </h3>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            2 Abiriba St, Uzuokoli Road, Umuahia, 440233, Abia State • Coordinates: {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/20"
          >
            <Navigation className="w-4 h-4 fill-current" /> Get Directions
          </a>
        </div>
      </div>

      {/* Main Map + Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
        {/* Leaflet / OSM Map Canvas Container */}
        <div className="lg:col-span-7 bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden relative min-h-[380px] sm:min-h-[420px] flex flex-col justify-between shadow-inner">
          {/* Address Lookup Bar */}
          <form onSubmit={handleSearchSubmit} className="absolute top-3 left-3 right-16 z-20 bg-slate-900/90 backdrop-blur-md rounded-xl border border-slate-700/80 p-1 flex items-center shadow-xl">
            <Search className="w-4 h-4 text-emerald-400 ml-2.5 shrink-0" />
            <input
              type="text"
              value={addressQuery}
              onChange={(e) => setAddressQuery(e.target.value)}
              placeholder="Search address or landmark..."
              className="w-full bg-transparent text-xs text-white px-2 py-1.5 focus:outline-none placeholder-slate-500"
            />
            <button
              type="submit"
              disabled={isGeocoding}
              className="bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-bold px-3 py-1 rounded-lg text-[11px] uppercase tracking-wider transition-colors shrink-0 flex items-center gap-1"
            >
              {isGeocoding ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Find'}
            </button>
          </form>

          {/* react-leaflet Map Component */}
          {isMounted ? (
            <MapContainer
              center={[coords.lat, coords.lng]}
              zoom={17}
              zoomControl={false}
              attributionControl={false}
              className="w-full h-full min-h-[380px] sm:min-h-[420px] z-10"
            >
              <MapRegister setMap={setMapInstance} />
              <MapController center={coords} />
              <TileLayer
                url={tileUrls[activeTileStyle].url}
                attribution={tileUrls[activeTileStyle].attribution}
                maxZoom={tileUrls[activeTileStyle].maxZoom}
              />
              <Marker position={[coords.lat, coords.lng]} icon={customIcon}>
                <Popup>
                  <div style={{ fontFamily: 'sans-serif', padding: '6px', color: '#0f172a', maxWidth: '240px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: '#059669', textTransform: 'uppercase', marginBottom: '2px' }}>
                      Access Technologies &amp; Renewable Energy
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', marginBottom: '4px', lineHeight: '1.3' }}>
                      2 Abiriba St, Uzuokoli Road
                    </div>
                    <div style={{ fontSize: '11px', color: '#475569', marginBottom: '8px' }}>
                      Umuahia, 440233, Abia State, Nigeria
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#0284c7', marginBottom: '10px' }}>
                      📞 Call: {phoneNumber}
                    </div>
                    <a href={directionsUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', width: '100%', textAlign: 'center', background: '#059669', color: '#ffffff', padding: '7px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '800', textDecoration: 'none', textTransform: 'uppercase' }}>
                      Get Directions ➔
                    </a>
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          ) : (
            <div className="w-full h-full min-h-[380px] sm:min-h-[420px] bg-slate-950 flex flex-col items-center justify-center text-slate-500 gap-3">
              <Compass className="w-8 h-8 text-emerald-500 animate-spin" />
              <span className="text-xs font-semibold tracking-wider uppercase">Loading interactive map...</span>
            </div>
          )}

          {/* Controls Bar Overlay */}
          <div className="absolute top-14 right-3 z-20 flex flex-col gap-1.5 bg-slate-900/90 backdrop-blur-md p-1.5 rounded-xl border border-slate-700/80 shadow-2xl">
            <button
              onClick={handleZoomIn}
              title="Zoom In"
              className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center transition-colors"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={handleZoomOut}
              title="Zoom Out"
              className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center transition-colors"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={handleRecenter}
              title="Recenter Map on Office"
              className="w-8 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          {/* Map Style Selector Overlay */}
          <div className="absolute bottom-3 left-3 z-20 bg-slate-900/90 backdrop-blur-md p-1.5 rounded-xl border border-slate-700/80 shadow-2xl flex items-center gap-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 hidden sm:inline-block">
              Style:
            </span>
            <button
              onClick={() => setActiveTileStyle('dark')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all ${
                activeTileStyle === 'dark' ? 'bg-emerald-500 text-slate-950 shadow-md' : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              Dark
            </button>
            <button
              onClick={() => setActiveTileStyle('streets')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all ${
                activeTileStyle === 'streets' ? 'bg-emerald-500 text-slate-950 shadow-md' : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              Streets
            </button>
            <button
              onClick={() => setActiveTileStyle('satellite')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all ${
                activeTileStyle === 'satellite' ? 'bg-emerald-500 text-slate-950 shadow-md' : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              Satellite
            </button>
          </div>

          {/* Floating Location Tag */}
          <div className="absolute bottom-3 right-3 z-20 bg-slate-900/90 backdrop-blur-md px-3 py-1.5 rounded-xl border border-emerald-500/30 text-xs shadow-xl flex items-center gap-2 max-w-[220px]">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <span className="font-bold text-white text-[10px] truncate">
              {resolvedLocationName}
            </span>
          </div>
        </div>

        {/* Location Contact Details Panel */}
        <div className="lg:col-span-5 space-y-4 flex flex-col justify-between">
          <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800 space-y-4">
            <div className="space-y-2">
              <span className="text-[11px] font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                <MapPin className="w-4 h-4" /> Physical Office Address
              </span>
              <div className="text-base sm:text-lg font-black text-white leading-snug">
                {fullAddress}
              </div>
              <p className="text-xs text-slate-400">
                Situated off Uzuokoli Road in Umuahia, Abia State. Visit us for engineering consultations, live battery testing, and inverter setup.
              </p>
            </div>

            <div className="pt-2 flex flex-col gap-2">
              <button
                onClick={handleCopyAddress}
                className="w-full bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white px-4 py-2.5 rounded-xl text-xs font-bold transition-all border border-slate-800 flex items-center justify-between"
              >
                <span className="truncate pr-2 font-mono text-[11px] text-emerald-400">{displayAddress}</span>
                <span className="flex items-center gap-1 text-[11px] text-amber-400 shrink-0">
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied!' : 'Copy Address'}
                </span>
              </button>

              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-md shadow-emerald-500/10"
              >
                <Navigation className="w-4 h-4 fill-current" /> Open Directions on Google Maps
              </a>
            </div>
          </div>

          {/* Quick Helpline Box */}
          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Direct Line</span>
                <a href={`tel:+2348064752074`} className="text-sm font-black text-white hover:text-amber-300">
                  {phoneNumber}
                </a>
              </div>
            </div>

            <div className="text-right">
              <span className="text-[10px] text-emerald-400 font-bold block flex items-center justify-end gap-1">
                <Clock className="w-3 h-3" /> Mon - Sat: 8AM - 6PM
              </span>
              <span className="text-[10px] text-slate-400 block">Umuahia, Abia State</span>
            </div>
          </div>

          {/* COREN Certification Tag */}
          <div className="px-4 py-3 rounded-xl bg-emerald-950/30 border border-emerald-500/20 flex items-center justify-between text-xs text-slate-300">
            <span className="flex items-center gap-1.5 font-bold text-emerald-300 text-[11px]">
              <ShieldCheck className="w-4 h-4 text-emerald-400" /> COREN Registered Engineering Hub
            </span>
            <span className="text-[10px] font-mono text-slate-400">GPS: {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
